/**
 * Company call-log paste import (v4.87.0).
 *
 * The client platform exports rows like:
 *   Customer ID | Call Date | Call Start | Duration (Minutes) | Billable | Dropped | Pay
 *   1178        | 09/08/2026| 09:35 AM   | 10                 | Yes      | No      | $0.00
 *
 * Paste may be TSV (spreadsheet copy), CSV, one-record-per-line (spaces), or
 * one-field-per-line (client app list copy). This module is pure:
 * parse pasted text -> per-day summaries -> merge into app state.
 * Company log is source of truth: import OVERWRITES stored per-day totals.
 */

export const SHIFT_START_HOUR = 9;
export const SHIFT_END_HOUR = 18;
export const SHIFT_WINDOW_MINS = (SHIFT_END_HOUR - SHIFT_START_HOUR) * 60; // 540

/** Undo snapshot for the last applied paste (v4.160.0). */
export const CALLLOG_UNDO_KEY = 'catintassist_calllog_undo_v1';

const splitRow = (line, delim) => {
  if (delim !== ',') return line.split('\t').map((c) => c.trim());
  // Minimal quote-aware CSV split (Pay may contain "$1,234.00").
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i += 1; }
      else inQ = !inQ;
    } else if (ch === ',' && !inQ) {
      out.push(cur.trim()); cur = '';
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
};

const isHeaderRow = (cells) => /customer|call date|duration|billable/i.test(cells.join(' '));

const parseDateTime = (dateStr, timeStr) => {
  // MM/DD/YYYY + "hh:mm AM/PM" (local time).
  const dm = String(dateStr || '').match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  const tm = String(timeStr || '').match(/(\d{1,2}):(\d{2})\s*([AaPp])\.?\s*[Mm]?\.?/);
  if (!dm || !tm) return null;
  let h = Number(tm[1]) % 12;
  if (/p/i.test(tm[3])) h += 12;
  const d = new Date(Number(dm[3]), Number(dm[1]) - 1, Number(dm[2]), h, Number(tm[2]), 0, 0);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
};

const isYes = (v) => /^\s*(y(es)?|true|1)\s*$/i.test(String(v || ''));

/**
 * Fallback for plain space-separated paste (chat/monitor copy with no tabs):
 *   10615 09/08/2026 10:41 AM 29 Yes No $0.00
 * Customer ID has no spaces; Call Start may be "10:41 AM" (two tokens).
 */
const parseSpaceRow = (line) => {
  const toks = String(line || '').trim().split(/\s+/);
  const dateIdx = toks.findIndex((t) => /\b\d{1,2}\/\d{1,2}\/\d{4}\b/.test(t));
  if (dateIdx < 0) return null;
  const dateStr = toks[dateIdx];
  let startStr = toks[dateIdx + 1] || '';
  let restIdx = dateIdx + 2;
  // "10:41 AM" split across two tokens — rejoin the AM/PM marker.
  if (/^[AaPp]\.?[Mm]?\.?$/.test(toks[dateIdx + 2] || '') && /:/.test(startStr)) {
    startStr = `${startStr} ${toks[dateIdx + 2]}`;
    restIdx = dateIdx + 3;
  }
  const [durStr, billableStr, droppedStr] = [toks[restIdx], toks[restIdx + 1], toks[restIdx + 2]];
  const startMs = parseDateTime(dateStr, startStr);
  const mins = Number.parseFloat(durStr);
  if (startMs === null || !Number.isFinite(mins) || mins < 0) return null;
  return {
    customerId: toks.slice(0, dateIdx).join(' ') || '',
    startMs,
    endMs: startMs + Math.round(mins * 60000),
    mins,
    billable: isYes(billableStr),
    dropped: isYes(droppedStr),
  };
};

/**
 * Vertical paste: each field on its own line (copy from the client app list) —
 *   91836 / 09/08/2026 / 12:17 PM / 19 / Yes / No / $0.00 / (next record...)
 * Regroups lines into one space-separated record string per call.
 * A record closes on a money token ("$0.00") or when a new date line arrives
 * after a full buffer (>=6 fields) — covers missing Pay values.
 */
const isDateTok = (t) => /\b\d{1,2}\/\d{1,2}\/\d{4}\b/.test(t);
const isMoneyTok = (t) => /\$\s?\d/.test(t);
const groupVerticalRecords = (lines) => {
  if (!lines.some(isDateTok)) return null; // not vertical format
  const records = [];
  let buf = [];
  lines.forEach((l) => {
    if (isDateTok(l) && buf.length >= 6) { records.push(buf.join(' ')); buf = []; }
    buf.push(l);
    if (isMoneyTok(l)) { records.push(buf.join(' ')); buf = []; }
  });
  if (buf.length) records.push(buf.join(' '));
  return records;
};

/** Parse pasted text -> { calls, skipped, total }. calls: billable-aware, sorted by start. */
export const parseCallLogText = (text) => {
  // Normalize: unicode spaces (client app copy uses NBSP) + any line ending style.
  const clean = String(text || '')
    .replace(/[\u00A0\u2007\u202F\u2009]/g, ' ')
    .replace(/\r\n?/g, '\n');
  let lines = clean.split('\n').map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { calls: [], skipped: 0, total: 0 };
  const hasTabs = lines.some((l) => l.includes('\t'));
  // Only treat commas as delimiters when a line actually has 4+ comma fields
  // (otherwise Pay "$0.00" or prose trips the CSV path and spaces never parse).
  const hasCsv = !hasTabs && lines.some((l) => l.split(',').length >= 4);
  const delim = hasTabs ? '\t' : hasCsv ? ',' : null;
  if (!delim) {
    // One-field-per-line paste: regroup into record strings first.
    // Only when NO line is already a complete space-separated row.
    if (!lines.some((l) => parseSpaceRow(l))) {
      const vertical = groupVerticalRecords(lines);
      if (vertical) lines = vertical;
    }
  }
  const calls = [];
  let skipped = 0;
  const skippedSamples = [];
  lines.forEach((line, idx) => {
    if (delim) {
      const cells = splitRow(line, delim);
      if (idx === 0 && isHeaderRow(cells)) return;
      if (cells.length >= 4) {
        const [customerId, dateStr, startStr, durStr, billableStr, droppedStr] = cells;
        const startMs = parseDateTime(dateStr, startStr);
        const mins = Number.parseFloat(durStr);
        if (startMs !== null && Number.isFinite(mins) && mins >= 0) {
          calls.push({
            customerId: customerId || '',
            startMs,
            endMs: startMs + Math.round(mins * 60000),
            mins,
            billable: isYes(billableStr),
            dropped: isYes(droppedStr),
          });
          return;
        }
      }
      // Delimited parse failed — fall through to the space-separated fallback.
    } else if (idx === 0 && isHeaderRow([line])) {
      return;
    }
    // Header with spaces ("Customer ID Call Date ...") has no date token.
    if (/customer/i.test(line) && /duration|billable/i.test(line)) return;
    const row = parseSpaceRow(line);
    if (row) calls.push(row);
    else {
      skipped += 1;
      if (skippedSamples.length < 3) skippedSamples.push(line.slice(0, 60));
    }
  });
  calls.sort((a, b) => a.startMs - b.startMs);
  return { calls, skipped, skippedSamples, total: lines.length };
};

const fmtTime = (ms) => {
  const d = new Date(ms);
  let h = d.getHours();
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${ap}`;
};

/** Group calls -> per-day summaries keyed for dailyLog/historyTimeline. */
export const groupCallsByDay = (calls) => {
  const map = new Map();
  calls.forEach((c) => {
    const dateStr = new Date(c.startMs).toDateString();
    if (!map.has(dateStr)) {
      map.set(dateStr, {
        dateStr,
        calls: 0,
        billableCalls: 0,
        billableMins: 0,
        totalMins: 0,
        firstStartMs: c.startMs,
        lastEndMs: c.endMs,
        segments: [],
      });
    }
    const day = map.get(dateStr);
    day.calls += 1;
    day.totalMins += c.mins;
    if (c.billable) { day.billableCalls += 1; day.billableMins += c.mins; }
    day.firstStartMs = Math.min(day.firstStartMs, c.startMs);
    day.lastEndMs = Math.max(day.lastEndMs, c.endMs);
    day.segments.push({ type: 'work', start: c.startMs, end: c.endMs });
  });
  return [...map.values()].map((d) => ({
    ...d,
    billableMins: Math.round(d.billableMins),
    totalMins: Math.round(d.totalMins),
    firstStart: fmtTime(d.firstStartMs),
    lastEnd: fmtTime(d.lastEndMs),
    // Off-time estimate inside the 9:00-18:00 window (breaks + avail, not tracked by company).
    offMinsEstimate: Math.max(0, SHIFT_WINDOW_MINS - Math.round(d.billableMins)),
  }));
};

/**
 * Merge day summaries into stored state. Pure — returns fresh objects.
 * - Past days: OVERWRITE dailyLog + historyTimeline; monthly/weekly get the DELTA
 *   (so re-imports never double-count).
 * - Today: imported values are AUTHORITATIVE for stats.dailyMinutes/callsToday
 *   (import is the correction tool — can go up or down). Monthly/weekly get the
 *   signed delta. Today is also written to dailyLog/historyTimeline so the
 *   progress-bar timeline repaints.
 * - dayStartTime fills from first call only when unset (drives lateness/shift math).
 */
export const mergeImportedDays = ({
  dailyLog = {},
  historyTimeline = {},
  stats = {},
  days = [],
  todayStr = new Date().toDateString(),
  currentMonthKey = `${new Date().getFullYear()}-${new Date().getMonth()}`,
} = {}) => {
  const nextLog = { ...dailyLog };
  const nextHistory = { ...historyTimeline };
  const nextStats = { ...stats };
  let totalMins = 0;
  let totalCalls = 0;
  let todayOld = null;
  let todayNew = null;
  days.forEach((d) => {
    totalMins += d.billableMins;
    totalCalls += d.billableCalls;
    if (d.dateStr === todayStr) {
      // Today: import overwrites (correction may go down). Re-import of the
      // same rows is still a no-op (delta 0).
      const prevDaily = Number(nextStats.dailyMinutes) || 0;
      todayOld = prevDaily;
      todayNew = d.billableMins;
      const delta = d.billableMins - prevDaily;
      nextStats.dailyMinutes = d.billableMins;
      nextStats.callsToday = d.billableCalls;
      const dayDate = new Date(d.firstStartMs);
      const dayMonthKey = `${dayDate.getFullYear()}-${dayDate.getMonth()}`;
      if (dayMonthKey === currentMonthKey) {
        nextStats.monthlyMinutes = Math.max(0, (nextStats.monthlyMinutes || 0) + delta);
        nextStats.weeklyMinutes = Math.max(0, (nextStats.weeklyMinutes || 0) + delta);
      }
      if (!nextStats.dayStartTime) nextStats.dayStartTime = d.firstStartMs;
      // Write today's segments too so the timeline repaints.
      nextLog[d.dateStr] = d.billableMins;
      nextHistory[d.dateStr] = d.segments.map((s) => ({ ...s }));
      return;
    }
    const old = Number(nextLog[d.dateStr] || 0);
    nextLog[d.dateStr] = d.billableMins;
    nextHistory[d.dateStr] = d.segments.map((s) => ({ ...s }));
    const dayDate = new Date(d.firstStartMs);
    const dayMonthKey = `${dayDate.getFullYear()}-${dayDate.getMonth()}`;
    if (dayMonthKey === currentMonthKey) {
      const delta = d.billableMins - old;
      nextStats.monthlyMinutes = (nextStats.monthlyMinutes || 0) + delta;
      nextStats.weeklyMinutes = (nextStats.weeklyMinutes || 0) + delta;
    }
  });
  return {
    dailyLog: nextLog,
    historyTimeline: nextHistory,
    stats: nextStats,
    summary: { days: days.length, totalMins, totalCalls, todayOld, todayNew },
  };
};

/**
 * v4.160.0 — "stored -> new" preview, per day, so the operator sees the change
 * BEFORE applying it. Pure.
 *
 * Stored minutes come from two different places on purpose:
 * - today: stats.dailyMinutes (the LIVE counter the scoreboard shows). dailyLog
 *   is only written at endDay/rollover, so reading dailyLog[today] would always
 *   say 0 and the diff would lie.
 * - past days: dailyLog[dateStr].
 *
 * Rows are sorted today-first, then newest day first.
 */
export const diffDaysAgainstStored = ({
  days = [],
  dailyLog = {},
  todayMinutes = 0,
  todayStr = new Date().toDateString(),
} = {}) => {
  const rows = (Array.isArray(days) ? days : []).map((d) => {
    const isToday = d.dateStr === todayStr;
    const storedMins = isToday
      ? Math.round(Number(todayMinutes) || 0)
      : Math.round(Number(dailyLog?.[d.dateStr]) || 0);
    const newMins = Math.round(Number(d.billableMins) || 0);
    return {
      dateStr: d.dateStr,
      isToday,
      billableCalls: d.billableCalls,
      calls: d.calls,
      billableMins: newMins,
      offMinsEstimate: d.offMinsEstimate,
      firstStart: d.firstStart,
      lastEnd: d.lastEnd,
      storedMins,
      newMins,
      delta: newMins - storedMins,
      // "had stored data" = the paste would overwrite something the app counted.
      overwrites: storedMins > 0,
    };
  });
  const dayMs = (d) => new Date(d.dateStr).getTime() || 0;
  return rows.sort((a, b) => {
    if (a.isToday !== b.isToday) return a.isToday ? -1 : 1;
    return dayMs(b) - dayMs(a);
  });
};

/** Only keep the days a paste actually touched — the snapshot stays tiny. */
export const buildUndoSnapshot = ({
  dailyLog = {},
  historyTimeline = {},
  stats = {},
  days = [],
  summary = null,
  savedAt = Date.now(),
} = {}) => {
  const prevLog = {};
  const prevHistory = {};
  (Array.isArray(days) ? days : []).forEach((d) => {
    const key = d?.dateStr;
    if (!key) return;
    prevLog[key] = dailyLog?.[key];
    prevHistory[key] = historyTimeline?.[key];
  });
  return { savedAt, touched: Object.keys(prevLog), prevLog, prevHistory, prevStats: { ...stats }, summary };
};

/**
 * v4.160.0 — reverse one applied paste. Pure. Restores the touched days to the
 * exact values they held before (including "was absent" -> delete the key) and
 * puts stats back wholesale, so monthly/weekly/daily all return in one move.
 */
export const applyUndoSnapshot = ({ dailyLog = {}, historyTimeline = {}, stats = {}, snapshot = null } = {}) => {
  if (!snapshot || !Array.isArray(snapshot.touched) || !snapshot.touched.length) {
    return { dailyLog, historyTimeline, stats, restored: false };
  }
  const nextLog = { ...dailyLog };
  const nextHistory = { ...historyTimeline };
  snapshot.touched.forEach((key) => {
    const log = snapshot.prevLog?.[key];
    if (log == null) delete nextLog[key];
    else nextLog[key] = log;
    const hist = snapshot.prevHistory?.[key];
    if (hist == null) delete nextHistory[key];
    else nextHistory[key] = hist;
  });
  return {
    dailyLog: nextLog,
    historyTimeline: nextHistory,
    stats: { ...snapshot.prevStats },
    restored: true,
  };
};

/** Human-readable "what would change" totals for the apply button. */
export const summarizeDiff = (rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  return {
    days: list.length,
    changedDays: list.filter((r) => r.delta !== 0).length,
    downMins: list.filter((r) => r.delta < 0).reduce((s, r) => s + r.delta, 0),
    upMins: list.filter((r) => r.delta > 0).reduce((s, r) => s + r.delta, 0),
    calls: list.reduce((s, r) => s + (r.billableCalls || 0), 0),
  };
};
